import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Inbox, AlertTriangle } from 'lucide-react';
import { ticketService } from '../../services/ticket.service';
import { useAuthStore } from '../../stores/authStore';

// Statuses that count as "in my queue" (everything not resolved).
const OPEN_STATUSES = ['new', 'in_progress', 'pending'];

const PRIORITY_COLOR: Record<string, string> = {
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

interface MyQueueWidgetProps {
  title?: string;
  loading?: boolean;
  className?: string;
}

/**
 * Agent's personal queue: their own tickets that are still open
 * (assigned to them, not resolved/closed/backtracked), newest activity first.
 * Self-contained — fetches via ticketService scoped to the current user.
 */
export function MyQueueWidget({ title, loading: parentLoading, className = '' }: MyQueueWidgetProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-queue', userId],
    queryFn: () =>
      ticketService.getTickets({
        // Use the PLURAL assigneeIds param (not singular assigneeId) so the BFF
        // does not widen an agent's scope with the unassigned queue — the widget
        // must show strictly the current user's own tickets. See tickets
        // controller applyScopeFilters (agent branch adds includeUnassigned only
        // when no assigneeIds is present).
        assigneeIds: userId,
        status: OPEN_STATUSES,
        limit: 8,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      }),
    enabled: !!userId,
    staleTime: 60_000,
  });

  const loading = parentLoading || isLoading;
  const tickets = data?.data ?? [];
  const shell = `border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`;

  if (loading) {
    return (
      <div className={shell}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse" />
        </div>
        <div className="flex-1 p-3 space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shell}>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <AlertTriangle className="h-8 w-8 text-red-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load your queue</p>
        </div>
      </div>
    );
  }

  return (
    <div className={shell}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || 'My Queue'}</h3>
        {tickets.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{data?.total ?? tickets.length} open</span>
        )}
      </div>

      {tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <Inbox className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Your queue is clear</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">No open tickets assigned to you</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
          {tickets.map((t) => (
            <div
              key={t.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/tickets/${t.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/tickets/${t.id}`); }}
              className="px-4 py-2.5 flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-900 dark:text-white truncate">{t.subject || '(no subject)'}</p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  #{t.ticketNumber} · {String(t.status).replace(/_/g, ' ')}
                </p>
              </div>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full capitalize shrink-0 ${
                  PRIORITY_COLOR[String(t.priority)] || PRIORITY_COLOR.low
                }`}
              >
                {t.priority}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
